import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { ProductFormSingleTextInput, ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';
import { StyledPlatformTag } from '../StyledOnboardingComponents';

const SocialConfigurationStep = ({
  small,
  offerings,
  socialPlatforms,
  setSocialPlatforms,
  socialMainPlatform,
  setSocialMainPlatform,
  socialMainLink,
  setSocialMainLink,
  visibleSocialPlatforms,
  platformLabels,
}) => {
  const { theme: { page, button } } = useAppTheme();

  if (!offerings.useSocial) {
    return (
      <Box gap="1rem">
        <Box gap="0.25rem">
          <Text weight={600} size="1.25rem" color={page.primaryText}>Social Platform Monitoring</Text>
          <Text size="0.85rem" color={page.secondaryText}>
            Select the platforms you&apos;d like us to scan for unauthorized content.
          </Text>
        </Box>
        <Box
          round="var(--app-radius)"
          pad="1rem"
          background={page.cardBg}
          gap="0.5rem"
        >
          <Text size="1rem" color={page.secondaryText}>
            <Text weight={700} as="span">Social monitoring is currently disabled.</Text>
            {' '}
            You can enable it in the
            {' '}
            <Text weight={700} as="span">Services</Text>
            {' '}
            step at any time.
          </Text>
        </Box>
      </Box>
    );
  }

  const renderPlatformTags = () => visibleSocialPlatforms.map((platformOption) => {
    const { id, label } = platformOption;
    const selected = socialPlatforms.includes(id);
    return (
      <StyledPlatformTag
        key={id}
        active={selected}
        highlightColor={button.primaryBg}
        hoverBg={button.selectBg}
        pad={{ vertical: '0.5rem', horizontal: '1rem' }}
        round="var(--app-radius)"
        onClick={() => {
          setSocialPlatforms((prev) => (
            prev.includes(id)
              ? prev.filter((platformId) => platformId !== id)
              : [...prev, id]
          ));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSocialPlatforms((prev) => (
              prev.includes(id)
                ? prev.filter((platformId) => platformId !== id)
                : [...prev, id]
            ));
          }
        }}
        tabIndex={0}
      >
        <Text
          size="1rem"
          weight={600}
          color={selected ? 'white' : page.primaryText}
        >
          {label}
        </Text>
      </StyledPlatformTag>
    );
  });

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Social Platform Monitoring</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Select the platforms you&apos;d like us to scan for unauthorized content.
        </Text>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box gap="0.25rem" margin={{ bottom: '0.5rem' }}>
          <Text size="1rem" color={page.primaryText} weight={600}>
            Select Platforms to Monitor
          </Text>
          {offerings.useCid && (
            <Text size="0.85rem" color={page.secondaryText}>
              YouTube is already covered under Content ID and is excluded here.
            </Text>
          )}
        </Box>
        <Box direction="row" wrap align="start" justify="start">
          {renderPlatformTags()}
        </Box>
        {socialPlatforms.length > 0 && (
          <Box margin={{ top: '0.5rem' }}>
            <Text size="0.85rem" color={page.secondaryText}>
              Selected platforms:
              {' '}
              {socialPlatforms
                .map((platformId) => platformLabels[platformId] || platformId)
                .join(', ')}
            </Text>
          </Box>
        )}
      </Box>
      {!offerings.useCid ? (
        <Box
          round="var(--app-radius)"
          pad="1rem"
          background={page.cardBg}
          gap="0.5rem"
        >
          <Box gap="0.25rem" margin={{ bottom: '0.5rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>
              Primary Platform (Optional)
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              Provide your main profile link so we can optimize monitoring accuracy.
            </Text>
          </Box>
          <Box
            wrap
            direction={small ? 'column' : 'row'}
            gap="small"
          >
            <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
              <Text size="1rem" color={page.primaryText} weight={600}>Platform</Text>
              <Box width={small ? '100%' : '80%'}>
                <ProductFormDropdownSelect
                  searchable
                  keepLabel
                  selectable
                  simpleOptions={visibleSocialPlatforms.map(
                    (platformOption) => platformOption.label,
                  )}
                  handleFormValues={(val) => setSocialMainPlatform(val)}
                  value={socialMainPlatform || ''}
                  label={socialMainPlatform || 'Select platform…'}
                  placeholder="Select platform…"
                  focusHighlight={page.focusHighlight}
                />
              </Box>
            </Box>
            <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
              <Text size="1rem" color={page.primaryText} weight={600}>Profile URL or Handle</Text>
              <Box width={small ? '100%' : '80%'}>
                <ProductFormSingleTextInput
                  key="socialMainLink"
                  focusHighlight={page.focusHighlight}
                  placeholder="https://..."
                  small={small}
                  value={socialMainLink || ''}
                  handleFormValues={(val) => setSocialMainLink(val)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          round="var(--app-radius)"
          pad="1rem"
          background={page.cardBg}
          gap="0.5rem"
        >
          <Text size="0.85rem" color={page.secondaryText}>
            With YouTube Content ID enabled, we will automatically reference the channel URLs
            {' '}
            you provide in the previous step to identify and monitor your content
            {' '}
            across other platforms.
          </Text>
        </Box>
      )}
    </Box>
  );
};

SocialConfigurationStep.propTypes = {
  small: PropTypes.bool.isRequired,
  offerings: PropTypes.shape({
    useSocial: PropTypes.bool.isRequired,
    useCid: PropTypes.bool.isRequired,
  }).isRequired,
  socialPlatforms: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSocialPlatforms: PropTypes.func.isRequired,
  socialMainPlatform: PropTypes.string.isRequired,
  setSocialMainPlatform: PropTypes.func.isRequired,
  socialMainLink: PropTypes.string.isRequired,
  setSocialMainLink: PropTypes.func.isRequired,
  visibleSocialPlatforms: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  platformLabels: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default SocialConfigurationStep;
